/**
 * Notification Center Component
 * Real-time notifications for sales, approvals, and updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import {
  Bell,
  BellRing,
  Check,
  CheckCheck,
  X,
  Trash2,
  DollarSign,
  ShoppingCart,
  CheckCircle,
  XCircle,
  UserPlus,
  MessageCircle,
  Settings,
  CreditCard,
  ChevronRight,
  Loader2,
  Inbox,
  Gift,
  Download,
  Music,
  Play,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Notification type icons and colors
const NOTIFICATION_CONFIG = {
  sale: {
    icon: DollarSign,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
  },
  purchase: {
    icon: ShoppingCart,
    color: 'text-violet-400',
    bgColor: 'bg-violet-500/20',
    borderColor: 'border-violet-500/30',
  },
  approval: {
    icon: CheckCircle,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
  },
  rejection: {
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/30',
  },
  follow: {
    icon: UserPlus,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    borderColor: 'border-cyan-500/30',
  },
  comment: {
    icon: MessageCircle,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
    borderColor: 'border-amber-500/30',
  },
  payout: {
    icon: CreditCard,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
  },
  gift: {
    icon: Gift,
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/20',
    borderColor: 'border-pink-500/30',
  },
  download: {
    icon: Download,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    borderColor: 'border-cyan-500/30',
  },
  system: {
    icon: Settings,
    color: 'text-zinc-400',
    bgColor: 'bg-zinc-500/20',
    borderColor: 'border-zinc-500/30',
  },
};

// Format time ago
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

// Single Notification Item
const NotificationItem = ({ notification, onMarkRead, onDelete, onNavigate }) => {
  const config = NOTIFICATION_CONFIG[notification.type] || NOTIFICATION_CONFIG.system;
  const Icon = config.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
        notification.read 
          ? 'bg-zinc-900/30 border-zinc-800 hover:bg-zinc-900/50' 
          : `${config.bgColor} ${config.borderColor} hover:opacity-90`
      }`}
      onClick={() => {
        if (!notification.read) onMarkRead(notification.id);
        if (notification.link) onNavigate(notification.link);
      }}
    >
      {/* Icon */}
      <div className={`w-8 h-8 rounded-full ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4 className={`text-sm font-medium ${notification.read ? 'text-zinc-400' : 'text-white'}`}>
            {notification.title}
          </h4>
          {!notification.read && (
            <div className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0 mt-1.5" />
          )}
        </div>
        <p className={`text-xs mt-0.5 line-clamp-2 ${notification.read ? 'text-zinc-500' : 'text-zinc-400'}`}>
          {notification.message}
        </p>
        <span className="text-xs text-zinc-500 mt-1 block">
          {formatTimeAgo(notification.created_at)}
        </span>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {!notification.read && (
          <button
            onClick={(e) => { e.stopPropagation(); onMarkRead(notification.id); }}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-white"
            title="Mark as read"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(notification.id); }}
          className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-red-400"
          title="Delete"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
};

// Main Notification Center Component
export const NotificationCenter = ({ className = '' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const panelRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  
  // Fetch notifications
  const fetchNotifications = useCallback(async (showLoading = false) => {
    if (!user) return;
    
    if (showLoading) setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/notifications?user_id=${user.id}&limit=20`);
      if (response.ok) {
        const data = await response.json();
        const newNotifications = data.notifications || [];
        
        // Check for new notifications
        if (notifications.length > 0 && newNotifications.length > 0) {
          const newestExisting = notifications[0]?.created_at;
          const newestNew = newNotifications[0]?.created_at;
          if (new Date(newestNew) > new Date(newestExisting)) {
            setHasNew(true);
          }
        }
        
        setNotifications(newNotifications);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user, notifications]);
  
  // Initial fetch and polling
  useEffect(() => {
    if (user) {
      fetchNotifications(true);
      
      // Poll every 30 seconds for new notifications
      pollIntervalRef.current = setInterval(() => {
        fetchNotifications(false);
      }, 30000);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user]);
  
  // Mark as read
  const handleMarkRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read?user_id=${user.id}`, {
        method: 'PUT',
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };
  
  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read-all?user_id=${user.id}`, {
        method: 'PUT',
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };
  
  // Delete notification
  const handleDelete = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}?user_id=${user.id}`, {
        method: 'DELETE',
      });
      
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (!notification?.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };
  
  // Clear all
  const handleClearAll = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/clear-all?user_id=${user.id}`, {
        method: 'DELETE',
      });
      
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };
  
  // Navigate to link
  const handleNavigate = (link) => {
    setIsOpen(false);
    navigate(link);
  };
  
  // Toggle panel
  const togglePanel = () => {
    setIsOpen(!isOpen);
    setHasNew(false);
    if (!isOpen) {
      fetchNotifications(true);
    }
  };
  
  if (!user) return null;
  
  return (
    <div className={`relative ${className}`} ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={togglePanel}
        className="relative p-2 rounded-lg hover:bg-zinc-800 transition-colors"
        data-testid="notification-bell"
      >
        {hasNew || unreadCount > 0 ? (
          <BellRing className="w-5 h-5 text-white" />
        ) : (
          <Bell className="w-5 h-5 text-zinc-400" />
        )}
        
        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs font-bold text-white flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        
        {/* Pulse animation for new notifications */}
        {hasNew && (
          <span className="absolute top-0 right-0 w-3 h-3 bg-violet-500 rounded-full animate-ping" />
        )}
      </button>
      
      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl overflow-hidden z-50"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <h3 className="font-semibold text-white">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Mark all read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="text-xs text-zinc-500 hover:text-red-400 flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Inbox className="w-12 h-12 text-zinc-700 mb-3" />
                  <p className="text-zinc-500">No notifications yet</p>
                  <p className="text-xs text-zinc-600 mt-1">
                    We'll notify you about sales, approvals, and updates
                  </p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  <AnimatePresence>
                    {notifications.map(notification => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkRead={handleMarkRead}
                        onDelete={handleDelete}
                        onNavigate={handleNavigate}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
            
            {/* Footer */}
            {notifications.length > 0 && (
              <div className="border-t border-zinc-800 p-2">
                <button
                  onClick={() => { setIsOpen(false); navigate('/dashboard'); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-violet-400 hover:bg-zinc-800 rounded-lg transition-colors"
                >
                  View all activity
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter;
